import { combineLatest, filter, first, map, Observable, of, Subscription, switchMap, take } from 'rxjs';
import { DOCUMENT, isPlatformBrowser, NgTemplateOutlet, ViewportScroller } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, Inject, input,
  OnDestroy, OnInit, PLATFORM_ID, Signal, signal, viewChild, ViewEncapsulation
} from '@angular/core';

import {
  ImageType, LanguageService, Product, ProductConnector, ProductService, ScriptLoader, VariantOption, WindowRef
} from '@spartacus/core';

import { CurrentProductService, PageSlotModule } from '@spartacus/storefront';

import {
  BrandsEnum, BrandThemesEnum, CoreModalService, IFootNote, ImageDsService, IPushCardWithLayout, ISkeeper, MarginEnum,
  PositionEnum, ScrollSpyService, SdComponentWrapperComponent, SdSpyTargetDirective, SdThemeWrapperComponent,
  SectionService, SectionTypeEnum, ThemesEnum, VideoDsService
} from '@seb/web-design-system/core';

import { decodeHTMLEntities, getPdpType, mapPageTemplateToPageType, waitForElm } from '@seb/web-common/core/utils';
import { SdSectionComponent } from '@seb/web-design-system/core/src/lib/components/section/section.component';
import { ArgumentsLayoutEnum, IArguments, SdArgumentsComponent } from '@seb/web-design-system/arguments';
import { CustomJsonLdDirective } from '@seb/web-common/core/directives';
import { BrandCornerMapper } from '@seb/web-common/core/mappers';

import {
  Bundle, CustomBaseStore, KlarnaConfig, OneyConfiguration, PdpConfig, RatingAndReviewsWidget, WC_CONFIG_PROVIDER, WcConfig
} from '@seb/web-common/core/models';

import {
  pageTypes, PdpTypeEnum, ProductScopeEnum, ScriptPositionEnum, SectionEnum, StockStatusEnum, WidgetTypes
} from '@seb/web-common/core/enums';

import {
  BaseStoreService, BundleService, HobStateService, KlarnaService, LazyLoadingService,
  LoaderService, LocationService, PrudsysService, TrackingService, UrlMappingService,
  WhatsInTheBoxService
} from '@seb/web-common/core/services';

import { ProductsAndBundlesComponent } from '../../components/products-and-bundles/products-and-bundles.component';
import { ProductRecipeListComponent } from '../../components/product-recipe-list/product-recipe-list.component';
import { ProductCrossSellComponent } from '../../components/product-cross-sell/product-cross-sell.component';
import { BenefitsCardPushComponent } from '../../components/benefits-card-push/benefits-card-push.component';
import { ProductOverviewComponent } from '../../components/product-overview/product-overview.component';
import { BundleOverviewComponent } from '../../components/bundle-overview/bundle-overview.component';
import { ProductReviewsComponent } from '../../components/product-reviews/product-reviews.component';
import { PdpComparatorComponent } from '../../components/pdp-comparator/pdp-comparator.component';
import { ProductHowToComponent } from '../../components/product-how-to/product-how-to.component';
import { ProductRangeComponent } from '../../components/product-range/product-range.component';
import { ProductFaqsComponent } from '../../components/product-faqs/product-faqs.component';
import { DesignedForComponent } from '../../components/designed-for/designed-for.component';
import { PdpFeaturesComponent } from '../../components/pdp-features/pdp-features.component';
import { IfuListComponent } from '../../components/ifu-list/ifu-list.component';
import { ProductDetailsPageService } from './product-details-page.service';

type ProductPosition = PositionEnum.left | PositionEnum.right | PositionEnum.center;
type ProductTheme = ThemesEnum | BrandThemesEnum;

type ProductUpdate = Pick<Product, 'price' | 'discountedPrice' | 'recommendedPrice' | 'strikethroughPrice' | 'url' | 'stock'>;

@Component({
  selector: 'wc-product-details-page',
  templateUrl: './product-details-page.component.html',
  styleUrls: ['./product-details-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    BundleOverviewComponent,
    ProductsAndBundlesComponent,
    SdThemeWrapperComponent,
    PageSlotModule,
    SdArgumentsComponent,
    ProductOverviewComponent,
    ProductCrossSellComponent,
    BenefitsCardPushComponent,
    ProductRangeComponent,
    ProductRecipeListComponent,
    PdpFeaturesComponent,
    ProductHowToComponent,
    PdpComparatorComponent,
    DesignedForComponent,
    IfuListComponent,
    ProductFaqsComponent,
    SdComponentWrapperComponent,
    NgTemplateOutlet,
    ProductReviewsComponent,
    SdSpyTargetDirective,
    CustomJsonLdDirective,
    SdSectionComponent
  ],
  providers: [
    ProductDetailsPageService,
    CoreModalService
  ]
})
export class ProductDetailsPageComponent implements OnInit, OnDestroy {
  class = input('');

  slotWhatsInTheBox = viewChild('slotWhatsInTheBox', { read: ElementRef });

  shouldRenderWhatsInTheBox = signal(true);

  argumentsProductInformation: IArguments;
  themesConfigProductInformation: ProductTheme = ThemesEnum.promo;
  layoutTextConfigProductInformation: ProductPosition = PositionEnum.left;
  textConfigProductInformation: ProductPosition = PositionEnum.left;

  bundleArgumentsProductInformation: IArguments;
  bundleThemesConfigProductInformation: ProductTheme = BrandThemesEnum.brand;
  bundleLayoutTextConfigProductInformation: ProductPosition = PositionEnum.right;
  bundleTextConfigProductInformation: ProductPosition = PositionEnum.left;

  argumentsFreeEditorialLocal: IArguments;
  themesConfigFreeEditorialLocal: ProductTheme = ThemesEnum.default;
  layoutTextConfigFreeEditorialLocal: ProductPosition = PositionEnum.right;
  textConfigFreeEditorialLocal: ProductPosition = PositionEnum.center;

  argumentsFreeEditorialBU: IArguments;
  argumentsFreeEditorialBU2: IArguments;
  themesConfigFreeEditorialBU: ProductTheme = ThemesEnum.default;
  themesConfigFreeEditorialBU2: ProductTheme = ThemesEnum.default;
  layoutTextConfigFreeEditorialBU: ProductPosition = PositionEnum.left;
  layoutTextConfigFreeEditorialBU2: ProductPosition = PositionEnum.left;
  textConfigFreeEditorialBU: ProductPosition = PositionEnum.left;
  textConfigFreeEditorialBU2: ProductPosition = PositionEnum.left;
  isSmall = false;

  argumentsLayout: ArgumentsLayoutEnum = ArgumentsLayoutEnum.default;
  marginEnum = MarginEnum;

  bundle: Bundle;
  product: Product;
  isAccessory: boolean;
  productCode: string;

  currentPage: PdpTypeEnum;
  currentPageType: pageTypes;
  pdpTypeEnum = PdpTypeEnum;

  disclaimerContent: IFootNote[];
  isRangeEmpty = false;
  hasRecipes = true;
  hasAccessories = true;
  hasReplacementParts = true;
  hasProductBundles: boolean;
  hasProductHowTo: boolean;
  hasProductIfu: boolean;
  hasProductFaqs: boolean;
  previousProductCode;
  benefitsPushs: IPushCardWithLayout[] = [];
  lameConfig: PdpConfig;
  showBenefits: boolean;
  isRecipesEnabled = true;
  objSkeeper: ISkeeper = {};
  defaultLng: string;
  showSkeeper = true;
  showRatingAnReviews: boolean;
  subscriptions = new Subscription();
  isSSR = true;
  hasOverviewLoaded = false;
  isLoading = false;
  brandCorner = BrandsEnum.default;
  baseStore: CustomBaseStore;
  useLazyLoad = false;
  brandSite: string;
  snippetsReviews;
  images = [];
  videos = [];
  snippetsImage = [];
  snippetsVideos = [];
  SectionTypeEnum = SectionTypeEnum;
  sectionModalIsOpened = false;
  reviewTitle: string;
  whatsInTheBoxTitle: string;
  characteristicsTitle: string;
  productClassifications;

  readonly isHob: Signal<boolean>;

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      'wc-template',
      this.isHob() ? 'is-active-brand' : ''
    ].filter(Boolean).join(' ');
  }

  constructor(
    private readonly currentProductService: CurrentProductService,
    private readonly productService: ProductService,
    private readonly locationService: LocationService,
    private readonly prudsysService: PrudsysService,
    private readonly route: ActivatedRoute,
    private readonly cdRef: ChangeDetectorRef,
    private readonly scrollSpyService: ScrollSpyService,
    private readonly bundleService: BundleService,
    private readonly urlMappingService: UrlMappingService,
    private readonly loaderService: LoaderService,
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller,
    private readonly baseStoreService: BaseStoreService,
    private readonly klarnaService: KlarnaService,
    private readonly lazyLoadingService: LazyLoadingService,
    private readonly winRef: WindowRef,
    private readonly scriptLoader: ScriptLoader,
    private readonly languageService: LanguageService,
    private readonly elementRef: ElementRef,
    private readonly trackingService: TrackingService,
    private readonly brandCornerMapper: BrandCornerMapper,
    private readonly imageDsService: ImageDsService,
    private readonly videoDsService: VideoDsService,
    private readonly pdpService: ProductDetailsPageService,
    private readonly translateService: TranslateService,
    private readonly productConnector: ProductConnector,
    private readonly hobStateService: HobStateService,
    private readonly sectionService: SectionService,
    private readonly whatsInTheBoxService: WhatsInTheBoxService,
    @Inject(WC_CONFIG_PROVIDER) private readonly wcConfig: WcConfig,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.isHob = toSignal(this.hobStateService.isHob$, { initialValue: false });
    this.isRecipesEnabled = this.wcConfig?.isRecipesEnabled ?? this.isRecipesEnabled;
    this.useLazyLoad = this.lazyLoadingService.isEnabled;
    this.baseStore = this.baseStoreService.baseStore;
    this.brandSite = this.baseStore.brandSite;
    this.showRatingAnReviews = this.baseStore.activateRatingAndReviews;
    this.showSkeeper = this.baseStore.activateRatingAndReviews;

    this.reviewTitle = this.translateService.instant('ratings.headline');
    this.whatsInTheBoxTitle = this.translateService.instant('pdp.sectionTitles.whatsInTheBoxTitle');
    this.characteristicsTitle = this.translateService.instant('pdp.sectionTitles.characteristics');

    this.whatsInTheBoxService.hasContent().subscribe((result) => this.shouldRenderWhatsInTheBox.set(result));
  }

  ngOnInit(): void {
    this.lazyLoadingService.setFirstUids(['ProductOverview']);

    if (isPlatformBrowser(this.platformId)) {
      this.scrollSpyService.spy({ thresholdBottom: 300, thresholdTop: 60 });
      this.removeSkeepers();
    }

    this.subscriptions.add(
      combineLatest([
        this.languageService.getActive(),
        this.klarnaService.getKlarnaConfig(),
        this.baseStoreService.getOneyConfiguration(),
        this.route.queryParamMap,
        this.route.paramMap
      ]).pipe(
        switchMap(([lang, klarnaConfig, oneyConfig, queryParams, params]) => combineLatest([
          ...this.refreshData(lang, params, queryParams),
          of(klarnaConfig),
          of(oneyConfig)
        ])),
        filter(([bundle, product]) => (
          !!bundle || !!product
        ))
      ).subscribe(([bundle, product, klarnaConfig, oneyConfig]) => {
        this.bundle = bundle;
        this.product = product;
        this.isLoading = false;

        if (oneyConfig?.length) {
          this.setOneyData(bundle ?? product, oneyConfig);
        }

        if (klarnaConfig) {
          this.setKlarnaData(bundle ?? product, klarnaConfig);
        }

        if (this.product) {
          this.processReceivedProduct();
        }

        this.loaderService.hideGlobalLoader();
        this.cdRef.markForCheck();
      })
    );

    this.listenToVideoEvents();
    this.listenToImagesEvents();
  }

  ngOnDestroy(): void {
    this.scrollSpyService.stopSpying();
    this.subscriptions.unsubscribe();
  }

  setIsRangeEmpty(event: boolean): void {
    this.isRangeEmpty = event;
  }

  setHasRecipes(event: boolean): void {
    this.hasRecipes = event;
  }

  setHasAccessories(event: boolean): void {
    this.hasAccessories = event;
  }

  setHasReplacementParts(event: boolean): void {
    this.hasReplacementParts = event;
  }

  onOverviewLoaded(): void {
    this.lazyLoadingService.setUidLoaded('ProductOverview');
    this.hasOverviewLoaded = true;
  }

  scrollToHash(hash: string): void {
    if (isPlatformBrowser(this.platformId) && this.winRef.location?.hash === `#${hash}`) {
      setTimeout(() => this.viewportScroller.scrollToAnchor(`${hash}_hash`), 2000);
    }
  }

  onSectionModalOpenChange(event: boolean): void {
    this.sectionModalIsOpened = event;
  }

  /**
   * Request product or bundle data
   * @param lang The active language
   * @param queryParams Url parameters
   * @returns An observable
   */
  private refreshData(lang: string, params: ParamMap, queryParams: ParamMap): [Observable<Bundle>, Observable<Product>] {
    const bundleCode = queryParams.get('bundle');

    this.isLoading = true;
    this.bundle = undefined;
    this.product = undefined;
    this.hasOverviewLoaded = false;
    this.productCode = params.get('productCode');
    this.previousProductCode = queryParams.get('productCode');
    this.currentPage = getPdpType(this.route.snapshot.url[0]?.path);
    this.defaultLng = lang.substring(0, 2) + '-' + lang.substring(0, 2).toUpperCase();
    this.showBenefits = (this.currentPage === PdpTypeEnum.PRODUCT) || (this.currentPage === PdpTypeEnum.ACCESSORY);

    this.cdRef.markForCheck();

    return [
      bundleCode ? this.getBundle(bundleCode) : of(undefined),
      bundleCode ? of(undefined) : this.getProduct(this.productCode)
    ];
  }

  /**
   * Ask for bundle detailled information
   * @returns An observable bundle or undefined if inconsistency detected.
   */
  private getBundle(bundleCode: string): Observable<Bundle | undefined> {
    return this.bundleService.getBundle(bundleCode, 'bundle', true).pipe(
      filter((bundle) => {
        if (!this.canOrderBundle(bundle)) {
          this.router.navigateByUrl(bundle.products[0].url);
          return false;
        }
        return true;
      }),
      map((bundle) => {
        this.bundleArgumentsProductInformation = this.pdpService.toBundleArgumentsProductInformation(bundle, this.lameConfig);
        this.setConfig(bundle.products[0], 'BundleDetailsPageTemplate');
        return bundle;
      })
    );
  }

  /**
   * Ask for product detailled information
   * @returns An observable product or undefined if inconsistency detected.
   */
  private getProduct(productCode: string): Observable<Product | undefined> {
    return this.currentProductService.getProduct().pipe(
      filter((product) => (
        product?.code === productCode
      )),
      map((product) => {
        this.setConfig(product, 'ProductDetailsPageTemplate');

        this.hasAccessories = true;
        this.hasProductHowTo = !!product?.howTo;
        this.hasProductBundles = product?.bundles?.length > 0;
        this.hasProductFaqs = product?.faqs?.faqEntries?.length > 0;
        this.hasProductIfu = !!product?.instructionsForUse?.instructionsForUse;

        this.productClassifications = product.classifications?.filter((classification) => classification.type !== 'USER_FRIENDLY');

        return product;
      })
    );
  }

  /**
   * Process the received product information
   */
  private processReceivedProduct(): void {
    if (this.product.star) {
      this.argumentsLayout = ArgumentsLayoutEnum.divided;
      this.layoutTextConfigFreeEditorialLocal = PositionEnum.right;
      this.layoutTextConfigFreeEditorialBU = PositionEnum.right;
      this.layoutTextConfigFreeEditorialBU2 = PositionEnum.left;
      this.layoutTextConfigProductInformation = PositionEnum.right;
    }

    if (this.product.claim) {
      this.product.claim = decodeHTMLEntities(this.product.claim);
    }

    if (isPlatformBrowser(this.platformId)) {
      this.updatePrice(this.product);
      this.adjustBrowserLocation();
    }

    if (this.showBenefits) {
      this.benefitsPushs = this.product.productBenefits?.map((productBenefit) => (
        this.pdpService.productBenefitToIPushCardWithLayout(productBenefit)
      ));
    }

    this.argumentsProductInformation = this.pdpService.toArgumentsProductInformation(this.product);

    this.argumentsFreeEditorialLocal = this.pdpService.toArgumentsFreeEditorial(
      this.product.freeEditorialLocal, '', this.lameConfig
    );

    this.argumentsFreeEditorialBU = this.pdpService.toArgumentsFreeEditorial(
      this.product.freeEditorialBU, 'pdp.sectionTitles.freeEditorial', this.lameConfig
    );

    this.argumentsFreeEditorialBU2 = this.pdpService.toArgumentsFreeEditorial2(
      this.product.freeEditorialBU, 'pdp.sectionTitles.freeEditorial', this.lameConfig
    );

    if (this.argumentsFreeEditorialBU?.content?.heading?.data?.description) {
      this.layoutTextConfigFreeEditorialBU2 = PositionEnum.right;
    }

    this.disclaimerContent = this.pdpService.toFootNoteDisclaimer(this.product);
    this.isAccessory = this.product.isAccessory;
    this.productCode = this.product.code;

    if (this.prudsysService.isEnabled()) {
      this.subscriptions.add(
        this.prudsysService.sendProductViewEvent(this.product.code).subscribe()
      );
    }
  }

  /**
   * See if bundle can be ordered
   * @param bundle The bundle to test
   * @returns true if it can be ordered, else false.
   */
  private canOrderBundle(bundle: Bundle): boolean {
    return bundle?.products.every((product) => (
      product.onSale && (product.price?.value !== null) && (product.price?.value !== undefined) &&
      (
        product.stock?.stockLevelStatus === StockStatusEnum.IN_STOCK ||
        product.stock?.stockLevelStatus === StockStatusEnum.LOW_STOCK
      )
    ));
  }

  /**
   * Adjust browser location, scroll to anchor if needed
   */
  private adjustBrowserLocation(): void {
    let translateUrl: string = undefined;
    let replacedUrl: string = undefined;

    switch (this.currentPage) {
      case PdpTypeEnum.IFU:
        translateUrl = '/instructions-for-use';
        break;

      case PdpTypeEnum.ACCESSORY:
        translateUrl = '/accessory';
        break;

      case PdpTypeEnum.PRODUCT:
        if (!this.product.isMarketing && !this.product.onSale) {
          if (!this.router.url.includes('csp') && this.product.fallbackURL) {
            this.router.navigateByUrl(this.product.fallbackURL);
          } else {
            this.router.navigateByUrl(this.product.urlCsp);
          }
          return;
        }
        if (this.router.url.includes('csp')) {
          if (this.router.url.includes('accessory') || (!this.product.isMarketing && !this.product.onSale)) {
            replacedUrl = this.product.urlCsp;
            translateUrl = '/accessory';
          } else {
            replacedUrl = this.product.urlIfu;
            translateUrl = '/instructions-for-use';
          }
        }
        if (this.product.fallbackURL) {
          this.router.navigateByUrl(this.product.fallbackURL);
          return;
        } else {
          replacedUrl = this.product.url;
        }
        break;
    }

    if (replacedUrl) {
      if (this.route.snapshot.fragment) {
        replacedUrl += '#' + this.route.snapshot.fragment;
        setTimeout(() => this.viewportScroller.scrollToAnchor(this.route.snapshot.fragment), 2200);
      }
      this.locationService.replaceUrl(replacedUrl);
    }

    if (translateUrl) {
      this.subscriptions.add(
        this.urlMappingService.getUrlMappingByCode(translateUrl).pipe(
          filter((u) => !!u)
        ).subscribe((urlMapping) => {
          this.urlMappingService.translateUrl(urlMapping);
        })
      );
    }
  }

  private getContentReviewWidget(pageType: pageTypes): RatingAndReviewsWidget {
    const ratingAndReviewsWidgets = this.baseStore.ratingAndReviewsWidgets?.find((reviewWidget) => (
      (reviewWidget.widgetType === WidgetTypes.SCRIPT) ? (
        reviewWidget.pagesTypes?.includes(pageType) &&
        reviewWidget.position?.includes(ScriptPositionEnum.PDP_BODY) &&
        reviewWidget.section?.includes(SectionEnum.RATING_AND_REVIEWS)
      ) : undefined
    ));

    return ratingAndReviewsWidgets?.content ? ratingAndReviewsWidgets : undefined;
  }

  /**
   * Remove Skeepers script if any
   */
  private removeSkeepers(): void {
    const element = this.document.getElementById('skeepers-script');
    element?.parentElement.removeChild(element);
  }

  /**
   * Load Skeepers script for the given product
   * @param product
   * @param contentReviewWidget
   */
  private setWidgetSkeepers(product: Product, currentPageType: pageTypes): void {
    this.showSkeeper = false;

    if (!isPlatformBrowser(this.platformId) || !product || product.productGenre === 'Forfait') {
      return;
    }

    const contentReviewWidget = this.getContentReviewWidget(currentPageType);
    if (!contentReviewWidget?.content) {
      return;
    }

    this.objSkeeper.productId = this.currentPage === this.pdpTypeEnum.ACCESSORY
      ? product?.commercialCode
      : product?.commercialCode?.slice(0, 5);

    this.objSkeeper.locale = this.defaultLng;
    this.objSkeeper.price = product.discountedPrice ? product.discountedPrice?.value : product.price?.value;
    this.objSkeeper.currency = product?.price?.currencyIso;
    this.objSkeeper.name = product?.name;

    if (this.currentPage === this.pdpTypeEnum.ACCESSORY) {
      this.objSkeeper.url = product?.urlCsp;
    } else if (this.currentPage === this.pdpTypeEnum.IFU) {
      this.objSkeeper.url = product?.urlIfu;
    } else {
      this.objSkeeper.url = product?.url;
    }

    this.objSkeeper.imageUrl =
      (!!product.images && product.images[ImageType.PRIMARY]?.['thumbnail']?.['url']) ||
      (!!product.images && product.images[ImageType.PRIMARY]?.['main']?.['url']) ||
      '';

    this.showSkeeper = true;

    waitForElm('.skeepers_product__stars').then(() => {
      this.initWidgetSkeepers(contentReviewWidget);
    });
  }

  private initWidgetSkeepers({ content, contentIntegrity }: RatingAndReviewsWidget): void {
    if (!this.scriptLoader.hasScript(content)) {
      this.scriptLoader.embedScript({
        src: content,
        attributes: contentIntegrity ? {
          integrity: contentIntegrity,
          crossOrigin: 'anonymous',
          id: 'skeepers-script'
        } : {
          id: 'skeepers-script'
        },
        callback: () => {
          this.scriptLoadedCallBack();
        },
        errorCallback: (error) => {
          console.debug(`Error loading ${content}`, error);
        }
      });
    } else {
      this.scriptLoadedCallBack();
    }
  }

  private scriptLoadedCallBack(): void {
    const skpBus = window['skpBus'];

    skpBus('on', 'rr:product_review_widget_search', (payload) => {
      this.trackingService.sendEvent('notationSearch', {
        facetFilterCriteria: payload['rating'],
        starsBy: payload['sort_criterion']
      });
    });

    skpBus('on', 'rr:product_review_widget_click', (payload) => {
      this.trackingService.sendEvent('clickSeeReviews', {
        productName: payload['rr_product_id']
      });
    });

    // TODO-EL: sauver le handle du add, ne pas refaire deux fois, utiliser le remove dans le destroy
    this.elementRef.nativeElement.querySelector('.c__title-wrapper .skeepers_product__stars')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.sectionService.dispatchClickSectionEvent(SectionTypeEnum.REVIEWS);
    });
  }

  private listenToVideoEvents(): void {
    this.subscriptions.add(
      this.videoDsService.loadVideoSnippets(this.brandSite).subscribe((videos) => {
        this.snippetsVideos = videos;
        this.cdRef.markForCheck();
      })
    );
  }

  private listenToImagesEvents(): void {
    this.subscriptions.add(
      this.imageDsService.loadImageSnippets().subscribe((images) => {
        this.snippetsImage = images;
        this.cdRef.markForCheck();
      })
    );
  }

  private setOneyData(productOrBundle: Product | Bundle, oneyConfig: OneyConfiguration[]): void {
    if (!productOrBundle?.discountedPrice) {
      return;
    }

    const filteredTypes = oneyConfig.filter(({ minAmount, maxAmount }) => (
      minAmount <= productOrBundle.discountedPrice.value && maxAmount >= productOrBundle.discountedPrice.value
    )).map(({ type }) => (
      type.replace(/[^0-9]+/, '')
    ));

    if (filteredTypes.length) {
      const paymentLabelExtended = this.translateService.instant('product.price.payment.labelExtended');
      const paymentLabel = this.translateService.instant('product.price.payment.label');
      const isOneyX12 = filteredTypes.some((item) => item.includes('12'));

      productOrBundle.dividedData = {
        price: (productOrBundle.discountedPrice.value / +filteredTypes[0]).toFixed(2) + '',
        paymentLabel: paymentLabel && paymentLabel !== 'product.price.payment.label' ? paymentLabel : undefined,
        paymentLabelExtended: paymentLabelExtended && paymentLabelExtended !== 'product.price.payment.labelExtended' && isOneyX12
          ? paymentLabelExtended
          : undefined
      };
    }
  }

  private setKlarnaData(bundleOrProduct: Product | Bundle, klarnaConfig: KlarnaConfig): void {
    const priceToUse: number = bundleOrProduct.discountedPrice
      ? bundleOrProduct.discountedPrice.value
      : bundleOrProduct.price?.value;

    if (klarnaConfig !== null && priceToUse >= klarnaConfig.minAmount && priceToUse <= klarnaConfig.maxAmount) {
      bundleOrProduct.klarnaContext = {
        locale: klarnaConfig.locale,
        price: priceToUse * 100,
        jsURL: klarnaConfig.jsURL,
        clientId: klarnaConfig.clientId
      };
    }
  }

  private setConfig(product: Product, template: string): void {
    const currentUrl = this.router.url;

    this.snippetsReviews = product && !currentUrl.includes('csp') ? this.pdpService.toSnippets(product, this.baseStore.brand_SF) : {};
    this.currentPageType = mapPageTemplateToPageType({ template }, this.router.parseUrl(currentUrl));
    this.setWidgetSkeepers(product, this.currentPageType);

    this.brandCorner = BrandsEnum.default;
    if (!product.isAccessory) {
      if (this.baseStoreService.getBrand(product.brand)) {
        this.brandCorner = product.brand;
      }
    }

    this.lameConfig = this.brandCornerMapper.toWcConfig(this.brandCorner)?.pdp;
    this.themesConfigFreeEditorialLocal = this.lameConfig?.freeEditoLocalArguments?.theme || ThemesEnum.light;
    this.themesConfigProductInformation = this.lameConfig?.productInfoArguments?.theme || ThemesEnum.promo;
  }

  private updatePrice(product: Product) {
    let refurbishedCode: string;

    if (product.variantOptions?.length) {
      // baseProduct is the product code of the new product when we're on a refurbished page
      if (product.baseProduct) {
        refurbishedCode = product.baseProduct;
      } else {
        const refurbishedVariant = product.variantOptions.find((variant) => (
          variant?.variantOptionQualifiers?.findIndex((q) => q.type === 'REFURBISHED' && q.value !== 'NEW') !== -1
        ));
        refurbishedCode = refurbishedVariant?.code;
      }
    }

    combineLatest([
      this.getSafeProductPriceAndStock(product.code),
      refurbishedCode ? this.getSafeProductPriceAndStock(refurbishedCode) : of(undefined)
    ]).subscribe(([productPriceAndStock, refurbishedPriceAndStock]) => {
      this.applyUpdatePrice(product, productPriceAndStock, refurbishedPriceAndStock);
    });
  }

  private applyUpdatePrice(product: Product, productPriceAndStock?: ProductUpdate, refurbishedPriceAndStock?: ProductUpdate) {
    if (productPriceAndStock) {
      this.product = {
        ...product,
        price: productPriceAndStock.price,
        discountedPrice: productPriceAndStock.discountedPrice,
        recommendedPrice: productPriceAndStock.recommendedPrice,
        strikethroughPrice: productPriceAndStock.strikethroughPrice,
        stock: productPriceAndStock.stock
      };

      const updatedProductVariants = this.product.variantOptions?.slice() || [];

      if (updatedProductVariants[0] && refurbishedPriceAndStock) {
        const { price, discountedPrice, recommendedPrice, strikethroughPrice, url } = refurbishedPriceAndStock;
        const updatedRefurbishedProduct = { price, discountedPrice, recommendedPrice, strikethroughPrice, url };

        updatedProductVariants[0] = {
          ...updatedProductVariants[0],
          product: updatedRefurbishedProduct
        } as VariantOption;

        this.product.variantOptions = updatedProductVariants;
      }
    }

    this.cdRef.markForCheck();
    this.isSSR = false;
  }

  /**
   * Get uncached product price and stock
   * @param code The product code
   * @returns The product with an updated price and stock
   */
  private getSafeProductPriceAndStock(code?: string): Observable<Product | undefined> {
    return code ? this.productService.hasError(code, ProductScopeEnum.PRICE).pipe(
      switchMap((err) => err ? of(undefined) : this.productConnector.get(code, ProductScopeEnum.PRICE_AND_STOCK).pipe(
        first((p) => !!p, null))
      ),
      take(1)
    ) : of(undefined);
  }
}
